'use client'

import cx from 'classnames'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { SwiperSlide } from 'swiper/react'

import Button from '@/components/Button'
import CustomerReview from '@/components/Review/CustomerReview'
import '@/components/Review/Review/Review.scss'
import ReviewDetail from '@/components/Review/ReviewDetail'
import Slider from '@/components/Slider'
import { ReviewRatingPercentageType, getReviews } from '@/serverActions/getReviews'
import { ReviewType } from '@/types/review'

type Props = {
  productId: string
  detail: boolean
  carousel: boolean
  perPage: number
  ageTag: boolean
}

export default function Review({ productId, detail, carousel, perPage, ageTag }: Props) {
  const maxAgeRange = 50

  const [reviews, setReviews] = useState<ReviewType[]>([])
  const [page, setPage] = useState(1)
  const [isNext, setIsNext] = useState(false)
  const [reviewSize, setReviewSize] = useState(0)
  const [reviewRating, setReviewRating] = useState(0)
  const [ageRange, setAgeRange] = useState<number | null>(null)
  const initialReviewRatingPercentage = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  }

  const clickAgeRange = useCallback(
    (selectAgeRange: number) => {
      setPage(1)
      setAgeRange(selectAgeRange == ageRange ? null : selectAgeRange)
    },
    [ageRange]
  )

  const [reviewRatingPercentage, setReviewRatingPercentage] =
    useState<ReviewRatingPercentageType>(initialReviewRatingPercentage)
  useEffect(() => {
    getReviews({
      productId,
      page,
      perPage,
      ...(ageRange != null
        ? {
            minAgeRange: ageRange,
            ...(ageRange != maxAgeRange ? { maxAgeRange: ageRange + 10 } : {}),
          }
        : {}),
    }).then((result) => {
      if (result != null) {
        setReviews((prevState) => {
          if (prevState[prevState.length - 1]?.reviewId == result.reviews[result.reviews.length - 1]?.reviewId) {
            return prevState
          }

          if (page == 1) {
            return result.reviews
          }

          return [...prevState, ...result.reviews]
        })
        setReviewSize(result.reviewSize)
        setIsNext(result.isNext)
        setReviewRating(result.reviewRating)
        setReviewRatingPercentage(result.reviewRatingPercentage)
      }
    })
  }, [productId, ageRange, page, perPage])
  if (reviews.length == 0 && !ageTag) {
    return <></>
  }

  return (
    <>
      <section className="c-review">
        {detail && (
          <ReviewDetail
            reviewSize={reviewSize}
            reviewRating={reviewRating}
            reviewRatingPercentage={reviewRatingPercentage}
          />
        )}

        {ageTag && (
          <>
            <div className={cx('c-review__tag', 'u-mb8')}>
              <div className={cx('c-review__tag-item', 'u-pr4')}>
                <Button
                  bgColor={ageRange == 20 ? 'rgba(6, 7, 37, 0.05)' : 'black'}
                  textColor={ageRange == 20 ? 'black' : 'white'}
                  width={'100%'}
                  onClick={() => clickAgeRange(20)}
                >
                  20代
                </Button>
              </div>
              <div className={cx('c-review__tag-item', 'u-pl4')}>
                <Button
                  bgColor={ageRange == 30 ? 'rgba(6, 7, 37, 0.05)' : 'black'}
                  textColor={ageRange == 30 ? 'black' : 'white'}
                  width={'100%'}
                  onClick={() => clickAgeRange(30)}
                >
                  30代
                </Button>
              </div>
            </div>
            <div className={cx('c-review__tag', 'u-mb20')}>
              <div className={cx('c-review__tag-item', 'u-pr4')}>
                <Button
                  bgColor={ageRange == 40 ? 'rgba(6, 7, 37, 0.05)' : 'black'}
                  textColor={ageRange == 40 ? 'black' : 'white'}
                  width={'100%'}
                  onClick={() => clickAgeRange(40)}
                >
                  40代
                </Button>
              </div>
              <div className={cx('c-review__tag-item', 'u-pl4')}>
                <Button
                  bgColor={ageRange == 50 ? 'rgba(6, 7, 37, 0.05)' : 'black'}
                  textColor={ageRange == 50 ? 'black' : 'white'}
                  width={'100%'}
                  onClick={() => clickAgeRange(50)}
                >
                  50代~
                </Button>
              </div>
            </div>
          </>
        )}
        {carousel ? (
          <Slider
            items={reviews.map((review) => {
              return (
                <SwiperSlide key={review.reviewId}>
                  <CustomerReview review={review} />
                </SwiperSlide>
              )
            })}
            carouselProps={{
              slidesPerView: 'auto',
              speed: 1000,
              clickable: true,
              navigation: {
                show: true,
                color: '#000',
              },
              pagenation: {
                show: true,
                color: '#000',
              },
            }}
          />
        ) : (
          <>
            <div>
              <div>
                {reviews.map((review) => {
                  return (
                    <div className="u-list-section-up__animation" key={review.reviewId}>
                      <CustomerReview review={review} />
                    </div>
                  )
                })}
              </div>

              {isNext && (
                <div className={'c-review__more'}>
                  <Button onClick={() => setPage((prevState) => prevState + 1)}>もっとみる</Button>
                </div>
              )}
            </div>
          </>
        )}
      </section>
    </>
  )
}
